import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { splitCombinedSearch } from '../../../utils/searchTermSplit';

/** Seitengröße der Trefferliste — der Server deckelt limit bei 100. */
export const CARD_BROWSER_PAGE_SIZE = 30;
const FILTER_DEBOUNCE_MS = 300;

/**
 * Filter-Objekt des Karten-Browsers:
 * `{ search, colors, types, oracleText, set, keywords, subtypes, ownedOnly,
 *    sortOption, sortDirection, groupBy }`.
 */
export const EMPTY_CARD_BROWSER_FILTERS = {
  search: '',
  colors: [],
  types: [],
  oracleText: '',
  set: '',
  keywords: '',
  subtypes: '',
  ownedOnly: false,
  sortOption: 'released',
  sortDirection: 'desc',
  groupBy: 'oracle',
};

const text = (value) => (typeof value === 'string' ? value.trim() : '');
const list = (value) => (Array.isArray(value) ? value : []);

/**
 * Query-Parameter für `GET /api/cards/search`. Pure Funktion, damit die Verteilung
 * der kombinierten Suche auf Name und Syntax testbar bleibt. Leere Werte bleiben weg —
 * jeder gesetzte Parameter ist serverseitig eine Bedingung.
 */
export function buildCardSearchParams(filters, { limit, offset }) {
  const params = new URLSearchParams();
  const { name, syntax } = splitCombinedSearch(text(filters.search));
  const put = (key, value) => { if (value) params.append(key, value); };

  put('nameSearchTerm', text(name));
  put('syntaxSearchTerm', text(syntax));
  put('oracleTextSearchTerm', text(filters.oracleText));
  put('keywordsSearchTerm', text(filters.keywords));
  put('typeSearchTerm', text(filters.subtypes));
  put('typeLineSearchTerms', list(filters.types).join(','));
  put('colorIdentitySearchTerms', list(filters.colors).join(','));
  put('setSearchTerms', text(filters.set));
  put('ownedOnly', filters.ownedOnly ? 'true' : '');
  put('sortOption', filters.sortOption);
  put('sortDirection', filters.sortDirection);
  put('groupBy', filters.groupBy);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  return params;
}

/**
 * Ist überhaupt eine Such-Bedingung gesetzt? Sortierung und Gruppierung zählen nicht:
 * ohne Bedingung liefert der Server ein leeres Ergebnis, die Anfrage wäre also nur
 * Last (Spiegel der Server-Regel in `cardSearchService.searchAllCards`). `ownedOnly`
 * zählt für sich allein als Bedingung — der Server grenzt damit auf den eigenen
 * Bestand ein und liefert auch ohne weiteren Filter Treffer.
 */
export function hasSearchConditions(filters) {
  const { name, syntax } = splitCombinedSearch(text(filters.search));
  return Boolean(
    text(name)
    || text(syntax)
    || text(filters.oracleText)
    || text(filters.keywords)
    || text(filters.subtypes)
    || text(filters.set)
    || list(filters.types).length > 0
    || list(filters.colors).length > 0
    || filters.ownedOnly === true
  );
}

/**
 * Nächster Offset für `useInfiniteQuery`'s `getNextPageParam`. `undefined` beendet die
 * Paginierung: bei einer leeren Seite (Server hat nichts mehr geliefert) oder wenn bereits
 * alle Treffer geladen sind (`loaded >= totalFilteredCards`).
 */
export function nextCardBrowserOffset(lastPage) {
  const loaded = (lastPage?.offset ?? 0) + (lastPage?.cards?.length ?? 0);
  if (!lastPage?.cards?.length) return undefined;
  if (typeof lastPage.totalFilteredCards === 'number' && loaded >= lastPage.totalFilteredCards) {
    return undefined;
  }
  return loaded;
}

/**
 * Seitenweise Suche über den gesamten Kartenbestand — geteilt vom mobilen
 * Hinzufügen-Modus und dem Karten-Katalog des Desktop-Editors.
 *
 * EIN Debounce über das gesamte Filter-Objekt (nicht je Feld): jede Änderung —
 * Tippen wie Pillen-Klick — schiebt dieselbe Wartezeit, damit nie zwei Anfragen für
 * einen zusammengehörigen Filterwechsel rausgehen. Die Serialisierung des Objekts ist
 * dabei Debounce-Auslöser, Query-Key und `filterToken` in einem; am Wechsel von
 * `filterToken` hängt der Aufrufer das Verwerfen seiner Kandidaten-Auswahl auf.
 */
export function useCardBrowserSearch({ filters }) {
  const filterKey = JSON.stringify(filters);
  const [debouncedKey, setDebouncedKey] = useState(filterKey);

  useEffect(() => {
    if (debouncedKey === filterKey) return undefined;
    const timer = setTimeout(() => setDebouncedKey(filterKey), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filterKey, debouncedKey]);

  const debouncedFilters = useMemo(() => JSON.parse(debouncedKey), [debouncedKey]);
  const isEmptyQuery = !hasSearchConditions(debouncedFilters);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['card-browser', debouncedKey],
    queryFn: ({ pageParam = 0, signal }) => {
      const params = buildCardSearchParams(debouncedFilters, {
        limit: CARD_BROWSER_PAGE_SIZE,
        offset: pageParam,
      });
      return json(`/api/cards/search?${params.toString()}`, { signal });
    },
    getNextPageParam: nextCardBrowserOffset,
    initialPageParam: 0,
    enabled: !isEmptyQuery,
    staleTime: 1000 * 60 * 5,
  });

  const entries = useMemo(
    () => (data?.pages || []).flatMap((page) => page.cards || []),
    [data]
  );

  const totalFilteredCards = typeof data?.pages?.[0]?.totalFilteredCards === 'number'
    ? data.pages[0].totalFilteredCards
    : entries.length;

  return {
    entries,
    totalFilteredCards,
    fetchNextPage,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    status,
    error,
    refetch,
    isEmptyQuery,
    filterToken: debouncedKey,
  };
}

export default useCardBrowserSearch;
